/**
 * DataGo — storefront tracker.
 *
 * Runs the Snowplow tracker (`sp.js`) on the real storefront page and sends every
 * storefront event, `page_view` included, straight to the DataGo collector.
 *
 * It used to hand events to the custom pixel instead, but the pixel's sandbox has
 * no `allow-same-origin`: Snowplow could not keep its cookie there, so
 * `user_pseudo_id` reset on every page. On the top frame the cookie persists and
 * the page URL is the real one. The pixel now owns only checkout, where theme JS
 * does not run — every event is sent from exactly one place.
 *
 * Requires `datago-context.liquid` to have run first (window.DataGoContext).
 */
(function () {
  const TRACKER_URL = 'https://stream.datago.ru/tracker/sp.js';
  const COLLECTOR_URL = 'https://stream.datago.ru/collector';
  const APP_ID = 'ru.limeshop.web.sp';
  const UID_STORAGE_KEY = 'datago_uid';
  const CART_FETCH_TIMEOUT = 3000;

  const context = window.DataGoContext || {};

  // The myshopify domain is only reached by bots and internal previews — real
  // visitors are redirected to the primary domain — and the theme editor is not a
  // visit either. Neither is reported; callers keep a working no-op API.
  const isReportable =
    !/\.myshopify\.com$/i.test(window.location.hostname) && !(window.Shopify && window.Shopify.designMode);

  if (!isReportable) {
    window.DataGo = Object.freeze({
      push: () => Promise.resolve(),
      viewOnce: () => false,
      refreshCart: () => Promise.resolve(),
      sessionFields: () => ({}),
    });
    return;
  }

  const firedViews = new Set();

  let cartState = {
    cart_id: null,
    cart_product_amount: typeof context.cart_product_amount === 'number' ? context.cart_product_amount : 0,
  };
  let cartRequest = null;
  let memoryUid = null;
  let trackerReady = false;

  /** @type {'pending'|'allowed'|'denied'} */
  let consent = 'pending';
  /** Events built before consent is known, sent once it is granted. */
  let pendingEvents = [];
  const PENDING_LIMIT = 50;

  /**
   * Stable visitor id in the 32 hex character format DataGo expects.
   *
   * The checkout pixel reads the same key through `browser.localStorage`, so
   * storefront and checkout events report one `uuid` per visitor.
   *
   * @returns {string}
   */
  const getUid = () => {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    const generated = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

    try {
      const stored = localStorage.getItem(UID_STORAGE_KEY);
      if (stored) return stored;

      localStorage.setItem(UID_STORAGE_KEY, generated);
      return generated;
    } catch (error) {
      memoryUid = memoryUid || generated;
      return memoryUid;
    }
  };

  /**
   * @returns {'mobile'|'tablet'|'desktop'}
   */
  const getDeviceType = () => {
    const ua = navigator.userAgent;

    if (/iPad|Tablet|PlayBook|Silk/i.test(ua)) return 'tablet';
    if (/Android/i.test(ua) && !/Mobile/i.test(ua)) return 'tablet';
    if (/Mobi|Android|iPhone|iPod|Windows Phone/i.test(ua)) return 'mobile';

    return 'desktop';
  };

  /**
   * Nothing is sent, and no cookie or visitor id is written, until the Customer
   * Privacy API says analytics is allowed. Where no regulation applies (GCC) that
   * is immediate; under GDPR it waits for the visitor's choice in the banner.
   * Events raised in the meantime are queued and flushed on consent, or dropped
   * on refusal.
   */
  const applyConsent = () => {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return;

    if (privacy.analyticsProcessingAllowed()) {
      consent = 'allowed';
      const queued = pendingEvents;
      pendingEvents = [];
      queued.forEach(send);
      return;
    }

    const choice = typeof privacy.currentVisitorConsent === 'function' ? privacy.currentVisitorConsent() : null;
    if (choice && choice.analytics === 'no') {
      consent = 'denied';
      pendingEvents = [];
    }
  };

  const watchConsent = () => {
    document.addEventListener('visitorConsentCollected', applyConsent);

    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (error) {
          console.error('[DataGo] consent API:', error);
          return;
        }
        applyConsent();
      });
    } else {
      applyConsent();
    }
  };

  /**
   * Loads `sp.js` and creates the tracker. Deferred until the first allowed send,
   * so Snowplow sets no cookie for a visitor who has refused analytics.
   */
  const ensureTracker = () => {
    if (trackerReady) return;
    trackerReady = true;

    (function (p, l, o, w, i, n, g) {
      if (!p[i]) {
        p.GlobalSnowplowNamespace = p.GlobalSnowplowNamespace || [];
        p.GlobalSnowplowNamespace.push(i);
        p[i] = function () {
          (p[i].q = p[i].q || []).push(arguments);
        };
        p[i].q = p[i].q || [];
        n = l.createElement(o);
        g = l.getElementsByTagName(o)[0];
        n.async = 1;
        n.src = w;
        g.parentNode.insertBefore(n, g);
      }
    })(window, document, 'script', TRACKER_URL, 'snowplow');

    window.snowplow('newTracker', 'sp', COLLECTOR_URL, {
      appId: APP_ID,
      maxPostBytes: 100000,
      maxLocalStorageQueueSize: 1000,
      resetActivityTrackingOnPageView: true,
      connectionTimeout: 5000,
      anonymousTracking: false,
      encodeBase64: false,
      discoverRootDomain: true,
      contexts: {
        webPage: true,
        session: true,
        performanceTiming: true,
        gaCookies: true,
        geolocation: false,
        clientHints: true,
      },
      retryStatusCodes: [],
      dontRetryStatusCodes: [],
    });

    // Snowplow's own `uid` field, kept alongside `user_context.userId`.
    window.snowplow('setUserId', context.user_id != null ? String(context.user_id) : null);
  };

  /**
   * DataGo takes the user from `user_context.userId` — the same place the RU site
   * puts it through GTM. Guests send an empty context.
   *
   * @returns {object}
   */
  const userContext = () => (context.user_id != null ? { userId: String(context.user_id) } : {});

  /**
   * @param {object} body - DataGo event body.
   */
  const dgSendEvent = (body) => {
    window.snowplow('trackSelfDescribingEvent', {
      event: { schema: 'iglu:ru.datago/event/jsonschema/1-0-0', data: body },
      context: [
        { schema: 'iglu:ru.datago/user_context/jsonschema/1-0-0', data: userContext() },
        { schema: 'iglu:ru.datago/ga4_user_params_context/jsonschema/1-0-0', data: {} },
      ],
    });
  };

  /**
   * The Liquid `cart` object exposes no cart token, so it is read from the Ajax
   * API. This is the only way `cart_id` is populated on a first add.
   *
   * @returns {Promise<void>}
   */
  const fetchCartState = async () => {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), CART_FETCH_TIMEOUT);

    try {
      const response = await fetch(`${cartUrl}.js`, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const cart = await response.json();
      cartState = {
        cart_id: cart.token || null,
        cart_product_amount: typeof cart.item_count === 'number' ? cart.item_count : 0,
      };
    } catch (error) {
      if (error.name !== 'AbortError') console.error('[DataGo] cart state:', error);
    } finally {
      clearTimeout(timeout);
    }
  };

  /**
   * @returns {Promise<void>}
   */
  const refreshCart = () => {
    if (cartRequest) return cartRequest;

    cartRequest = fetchCartState().finally(() => {
      cartRequest = null;
    });

    return cartRequest;
  };

  /**
   * Session block merged into the parameters of every storefront event.
   *
   * @returns {object}
   */
  const sessionFields = () => ({
    is_auth: context.is_auth === true,
    cart_id: cartState.cart_id,
    cart_product_amount: cartState.cart_product_amount,
    device_type: getDeviceType(),
    uuid: consent === 'allowed' ? getUid() : null,
    region: context.region ?? null,
    lime_region: context.region ?? null,
  });

  /**
   * Guards view style events so each one fires at most once per page.
   *
   * @param {string} key
   * @returns {boolean} True the first time the key is seen.
   */
  const viewOnce = (key) => {
    if (firedViews.has(key)) return false;

    firedViews.add(key);
    return true;
  };

  /**
   * @param {object} body - Event body without `uuid`, which is attached here so
   *   the visitor id is only created once analytics is allowed.
   */
  const send = (body) => {
    try {
      ensureTracker();
      dgSendEvent({ ...body, eventParams: { uuid: getUid(), ...body.eventParams } });
    } catch (error) {
      console.error('[DataGo] send failed:', body.event, error);
    }
  };

  /**
   * Sends a storefront event to DataGo, or queues it until consent is known.
   *
   * @param {string} event - DataGo event name.
   * @param {object} [eventParams] - Parameters specific to this event.
   * @param {object} [ecommerce] - Ecommerce block, when the event carries one.
   * @returns {Promise<void>}
   */
  const push = async (event, eventParams = {}, ecommerce = undefined) => {
    if (!event || consent === 'denied') return;

    if (cartRequest) await cartRequest;

    const { uuid, ...session } = sessionFields();
    const body = {
      eventStructure: 'GA4',
      event,
      user_id: context.user_id ?? null,
      eventParams: { ...session, ...eventParams },
    };
    if (ecommerce) {
      body.ecommerce = { currency: context.currency ?? null, ...ecommerce };
    }

    if (consent === 'allowed') {
      send(body);
    } else if (pendingEvents.length < PENDING_LIMIT) {
      pendingEvents.push(body);
    }
  };

  window.DataGo = Object.freeze({ push, viewOnce, refreshCart, sessionFields });

  watchConsent();

  // Warm the cart token first so `page_view` and the first cart event carry it.
  if (cartState.cart_product_amount) refreshCart();

  push('page_view', {
    page_title: document.title || null,
    page_location: window.location.href,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      refreshCart();
    });
  }
})();
